import { TFunction } from "react-i18next";
import { OrganizationAlert } from "../../../../../services/Api/Organization/organizationTypes";
import { getLicenseExpirationDays, isLicenseExpired } from "../../../../../services/license";
import { getLicensePaymentDueDays } from "../../Admin/Organization/services/alerts";

export const getOrganizationAlerts = (
	licenseExpiresAt: string,
	licenseType: string,
	paymentDueDate: string,
	t: TFunction,
	isAdminMode: boolean,
	abusedAccesses: number,
	outdatedTerminals?: number,
	testVersionTerminals?: number,
	organizationTimezone?: string
): OrganizationAlert[] => {
	const licenseExpirationAlert = getLicenseExpirationAlert(t, licenseExpiresAt, licenseType, organizationTimezone);
	const licensePaymentDueAlert = getLicensePaymentDueAlert(t, paymentDueDate);
	const manualLicenseAlert = getManualLicenseAlert(t, licenseType);
	const alerts: OrganizationAlert[] = [];

	if (outdatedTerminals && outdatedTerminals > 0) {
		alerts.push({
			title: `${t("modules.organization.field.outdated_terminals.title", {
				ns: "admin",
			})}: ${outdatedTerminals}`,
			type: "WARNING",
		});
	}
	if (testVersionTerminals && testVersionTerminals > 0) {
		alerts.push({
			title: `${t("modules.organization.field.test_version_terminals.title", {
				ns: "admin",
			})}: ${testVersionTerminals}`,
			type: "DANGER",
		});
	}

	if (licenseExpirationAlert) alerts.push(licenseExpirationAlert);
	if (licensePaymentDueAlert && isAdminMode) alerts.push(licensePaymentDueAlert);
	if (manualLicenseAlert && isAdminMode) alerts.push(manualLicenseAlert);

	if (abusedAccesses) {
		alerts.push({
			title: `${t("modules.organization.field.abused_access.title", {
				ns: "admin",
			})}: ${abusedAccesses}`,
			type: "DANGER",
		});
	}

	return alerts;
};

export const getManualLicenseAlert = (t: TFunction, licenseType: string) => {
	if (licenseType === "MANUAL") {
		return {
			title: t("modules.organization.field.manual_license.title", { ns: "admin" }),
		};
	}
};

export const getLicenseExpirationAlert = (
	t: TFunction,
	licenseExpiresAt: string,
	licenseType: string,
	organizationTimezone?: string
) => {
	if (isLicenseExpired(licenseExpiresAt, licenseType, organizationTimezone)) {
		const expirationDays = getLicenseExpirationDays(licenseExpiresAt, organizationTimezone);
		let alertText = "";
		let type: "WARNING" | "DANGER" | undefined = undefined;
		if (expirationDays !== undefined) {
			if (expirationDays <= 14) {
				alertText = t("modules.organization.field.expires_at.title", {
					ns: "admin",
					count: expirationDays,
				});
				type = "WARNING";
			}
			if (expirationDays >= -7 && expirationDays <= 0) {
				alertText = t("modules.organization.field.not_paid_and_blocked.title", {
					ns: "admin",
					count: 7 + expirationDays,
				});
				type = "DANGER";
			}
			if (expirationDays < -7) {
				alertText = t("modules.organization.field.not_paid.title", { ns: "admin" });
				type = "DANGER";
			}

			return {
				title: alertText,
				type,
			};
		}
	}
};

export const getLicensePaymentDueAlert = (t: TFunction, paymentDateDue: string) => {
	if (!paymentDateDue) return undefined;

	const paymentDueDays = getLicensePaymentDueDays(paymentDateDue);
	if (paymentDueDays !== undefined) {
		let alertText = "";
		let type: "WARNING" | "DANGER" | undefined = undefined;

		if (paymentDueDays <= 7) {
			alertText = t("modules.organization.field.payment_due_date_at.title", {
				count: paymentDueDays,
				ns: "admin",
			});
			type = "WARNING";
		}
		if (paymentDueDays === 0) {
			alertText = t("modules.organization.field.payment_due_date.title", { ns: "admin" });
			type = "DANGER";
		}
		if (paymentDueDays < 0) {
			alertText = t("modules.organization.field.payment_due_date_expired_at.title", {
				count: Math.abs(paymentDueDays),
				ns: "admin",
			});
			type = "DANGER";
		}

		return {
			title: alertText,
			type,
		};
	}
};
